import { atom, useAtomValue, useSetAtom } from 'jotai'
import type { Atom } from 'jotai'
import { atomEffect } from 'jotai-effect'
import { useCallback } from 'react'
import type { ReserveGuestAuthenticationType } from '~/lib/graphql/graphql'
import { cancelGuardCardErrorMessage$ } from './cancelGuard'
import {
  memberRequiredCardPaymentErrorMessage$,
  noCardRegisteredErrorMessage$,
} from './paymentMethod'
import { requestsAndCommentsError$ } from './requestsAndComments'
import { scope } from './scope'
import {
  agreedToReserveRequest$,
  isTrusted$,
  reservationInputStateValue$,
} from './stateMachine/context'
import { isInitialDisplay$, plan$, transition$, useTransition } from './stateMachine/state'
import {
  emailError$,
  hasBlacklistError$,
  ikyuPointUsageError$,
  isBlacklistChecked$,
  isMobilePhoneNumber$,
  passwordError$,
  reserverNameKanaError$,
  reserverPhoneNumberError$,
  visitorNameKanaError$,
  visitorPhoneNumberError$,
} from './userInput'

const errorMessages$ = atom((get) => {
  return [
    get(reserverNameKanaError$),
    get(reserverPhoneNumberError$),
    get(emailError$),
    get(passwordError$),
    get(visitorNameKanaError$),
    get(visitorPhoneNumberError$),
    get(ikyuPointUsageError$),
    get(requestsAndCommentsError$),
    get(noCardRegisteredErrorMessage$),
    get(cancelGuardCardErrorMessage$),
    get(memberRequiredCardPaymentErrorMessage$),
  ].filter(isString)
})
errorMessages$.debugLabel = 'errorMessages$'

function isString(v?: string): v is string {
  return v !== undefined && v !== ''
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('isString', () => {
    expect(isString('')).toBe(false)
    expect(isString()).toBe(false)
    expect(isString('a')).toBe(true)
  })
}

export function useErrorMessages() {
  return useAtomValue(errorMessages$)
}

type DisplayAuthMessage =
  | { type: 'None' }
  | { type: 'Show'; authType: ReserveGuestAuthenticationType }

const displayAuthMessage$: Atom<DisplayAuthMessage> = atom((get) => {
  return displayAuthMessage(get(isTrusted$), get(isMobilePhoneNumber$))
})
displayAuthMessage$.debugLabel = 'displayAuthMessage$'

export function useDisplayAuthMessage() {
  return useAtomValue(displayAuthMessage$)
}

function displayAuthMessage(isTrusted: boolean, isMobilePhoneNumber: boolean): DisplayAuthMessage {
  if (isTrusted) {
    return { type: 'None' }
  }
  if (isMobilePhoneNumber) {
    return { type: 'Show', authType: 'PHONE_NUMBER' }
  }
  return { type: 'Show', authType: 'EMAIL' }
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('displayAuthMessage', () => {
    expect(displayAuthMessage(false, false)).toEqual({ type: 'Show', authType: 'EMAIL' })
    expect(displayAuthMessage(false, true)).toEqual({ type: 'Show', authType: 'PHONE_NUMBER' })
    expect(displayAuthMessage(true, true)).toEqual({ type: 'None' })
    expect(displayAuthMessage(true, false)).toEqual({ type: 'None' })
  })
}

const validateRequestReservation$ = atom((get) => {
  const isRequestReservation = get(plan$)?.isRequestReservation
  if (isRequestReservation === undefined || !isRequestReservation) {
    return true
  }
  return get(agreedToReserveRequest$)
})
validateRequestReservation$.debugLabel = 'validateRequestReservation$'

// ../specification.md 「atomEffect について」参照
export const listenUserInputError$ = atomEffect((get, set) => {
  if (get(isInitialDisplay$)) {
    return
  }
  // state が変更されるたびに呼び出したいので state を参照する
  get(reservationInputStateValue$)
  // ブラックリストのチェックが完了するまでエラー有無の判断を延期する
  if (!get(isBlacklistChecked$)) {
    return
  }
  const errors = get(errorMessages$)
  if (errors.length > 0) {
    return
  }
  // リクエスト予約の場合のみ、同意文言にチェックしたかどうか
  // 確認する必要がある
  if (!get(validateRequestReservation$)) {
    return
  }
  set.recurse(transition$, { type: 'validated' })
})
listenUserInputError$.debugLabel = 'listenUserInputError$'

export function useSetAgreedToReserveRequest() {
  const send = useTransition()
  return useCallback(
    (v: boolean) => {
      send({ type: 'updated', agreedToReserveRequest: v })
    },
    [send]
  )
}

export const hasUserInteracted$ = scope.atom(false, 'hasUserInteracted$')

export function useMarkUserInteracted() {
  const set = useSetAtom(hasUserInteracted$)
  return useCallback(() => {
    set(true)
  }, [set])
}

const shouldShowErrors$ = atom((get) => {
  return get(hasUserInteracted$) || get(hasBlacklistError$)
})

export function useShouldShowErrors() {
  return useAtomValue(shouldShowErrors$)
}
